// Operative reports (Firestore collection "cros"). The texts "chirurgiens", "anesthesistes"…
// are what the former page, the exports and the search read: they stay the reference.
import { allows, rightsOf, unitsOf } from '../../lib/permissions';

export const SURGEONS = ['Pr Amadou Gabriel CISS', 'Pr Papa Salmane BA', 'Pr Momar Sokhna DIOP', 'Dr Papa Amath DIAGNE', 'Dr Papa Ousmane BA'];
export const ANESTHESIOLOGISTS = ['Dr Étienne Birame SENE', 'Dr El Hadj Boubacar BA', 'Dr El Hadj Malick DIAO', 'Dr Marie Victoire SÈNE', 'Dr Oulimata DIOP', 'Dr GUEYE'];
export const CEC_MANAGERS = ['Mr Samba Camara DIENE', 'Mr Omar SENGHOR', 'Mr DIEME', 'Mr SONKO'];
export const INSTRUMENTALISTS = ['Mr SADA', 'Mme Aïcha BARRO', 'Mme FALL', 'Mr Mass SARR', 'Mr Pape Demba', 'Mr DIALLO'];
export const DIAGNOSTICS = ['Insuffisance Mitrale', 'Rétrécissement Mitral', 'Insuffisance Aortique', 'Rétrécissement Aortique', 'Insuffisance Tricuspide', 'Insuffisance Pulmonaire', 'Sténose Pulmonaire', 'Plastie fuyante', 'Plastie stenosante', 'Prothèse fuyante', 'Prothèse stenosante', 'Désinsertion prothèse', 'Missmatch prothèse', 'Thrombose prothèse', 'Coronaropathie', 'Endocardite', 'Péricardite Chronique Constrictive', 'Membrane sous-aortique', 'HTAP', 'CIA', 'CIV', 'CAV', 'T4F', 'RVPA', 'PCA', 'Coarctation aorte', 'VDDI', 'Ventricule Unique', 'Transposition des Gros Vaisseaux', 'Malposition des Coronaires', 'Hemtome', 'Épanchement péricardique'];
export const INDICATIONS = ['Plastie Mitrale', 'Plastie aortique', 'Plastie tricuspide', 'Plastie pulmonaire', 'Remplacement valvulaire aortique', 'Remplacement valvulaire mitral', 'Remplacement valve tricuspide', 'Remplacement valve pulmonaire', 'DRV mitro-aortique', 'Pontage aorto-coronarien', 'Blalock', 'Cure'];

// Stored comment for each type (as written by the former page).
const COMMENTS = { Normal: 'Normal', 'Mission Canadienne': 'Mission Canadienne', 'Mission Suisse': 'Mission Suisse ' };
export const TYPES = Object.keys(COMMENTS);
export const TYPE_OPTIONS = [...TYPES.map((type) => ({ value: type, label: type })), { value: 'Autres', label: 'Autre' }];

const INTERNE = 'Interne et DES';

export const splitNames = (text) =>
  String(text || '')
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item && item !== 'Autres');

// Team and lists as edited in the form.
export const toForm = (cro = {}) => {
  const surgeonEntries = splitNames(cro.chirurgiens);
  const interneEntry = surgeonEntries.find((entry) => entry.startsWith(INTERNE));
  return {
    patientId: cro.patientId || '',
    datePatient: cro.datePatient ? String(cro.datePatient).slice(0, 10) : '',
    surgeons: surgeonEntries.filter((entry) => !entry.startsWith(INTERNE)),
    interne: interneEntry ? interneEntry.replace(/^Interne et DES\s*:?\s*/, '') : cro.interneDesName || '',
    anesthesiologists: splitNames(cro.anesthesistes),
    cecManagers: splitNames(cro.cecManagers),
    instrumentalists: splitNames(cro.instrumentalists),
    diagnostics: splitNames(cro.diagnos),
    indications: splitNames(cro.indicationOperatoire),
    type: cro.commentType && (TYPES.includes(cro.commentType) || cro.commentType === 'Autres') ? cro.commentType : 'Normal',
    customComment: cro.commentType === 'Autres' ? cro.customComment || cro.comment || '' : '',
    links: Array.isArray(cro.dropboxLinks) ? cro.dropboxLinks.map((entry) => (typeof entry === 'string' ? entry : entry && (entry.link || entry.lien))).filter(Boolean) : [],
    // Files of the service's Dropbox: [{ id, name, mimeType }].
    documents: Array.isArray(cro.documents) ? cro.documents.filter((item) => item && item.id) : [],
    unit: cro.service || '',
  };
};

// Fields written for the new pages, the former page and the exports alike.
export const toRecord = (form) => {
  const interne = form.interne.trim();
  const surgeons = [...form.surgeons, ...(interne ? [`${INTERNE}: ${interne}`] : [])];
  return {
    patientId: form.patientId,
    datePatient: form.datePatient,
    chirurgiens: surgeons.join(', '),
    selectedSurgeons: [...form.surgeons, ...(interne ? [INTERNE] : [])],
    interneDesName: interne,
    chirurgienCustom: '',
    anesthesistes: form.anesthesiologists.join(', '),
    selectedAnesthesiologists: form.anesthesiologists,
    anesthesisteCustom: '',
    cecManagers: form.cecManagers.join(', '),
    selectedCecManagers: form.cecManagers,
    cecManagerCustom: '',
    instrumentalists: form.instrumentalists.join(', '),
    selectedInstrumentalists: form.instrumentalists,
    instrumentalistCustom: '',
    diagnos: form.diagnostics.join(', '),
    selectedDiagnostics: form.diagnostics,
    diagnosticCustom: '',
    indicationOperatoire: form.indications.join(', '),
    selectedIndications: form.indications,
    indicationCustom: '',
    commentType: form.type,
    customComment: form.type === 'Autres' ? form.customComment.trim() : '',
    comment: form.type === 'Autres' ? form.customComment.trim() : COMMENTS[form.type],
    dropboxLinks: form.links,
    documents: (form.documents || []).map(({ id, name, mimeType }) => ({ id, name, mimeType: mimeType || '' })),
    service: form.unit,
  };
};

export const typeLabel = (cro) => (TYPES.includes(cro.commentType) ? cro.commentType : String(cro.comment || '').trim() || 'Normal');

export const searchText = (cro, patient) =>
  [patient && patient.nom, patient && patient.dossierNumber, cro.chirurgiens, cro.anesthesistes, cro.cecManagers, cro.instrumentalists, cro.diagnos, cro.indicationOperatoire, cro.comment]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

// Written by the surgeons of the unit (and the administration), as before.
export const croAccess = (user) => {
  const rights = rightsOf(user);
  const isAdmin = user.role === 'Admin';
  const myUnits = unitsOf(user.service);
  const ownsCro = (cro) => isAdmin || unitsOf(cro && cro.service).some((unit) => myUnits.includes(unit));
  return {
    isAdmin,
    myUnits,
    visible: ownsCro,
    canCreate: allows(rights, 'cro', 'modifier'),
    canEdit: (cro) => allows(rights, 'cro', 'modifier') && ownsCro(cro),
    canDelete: (cro) => allows(rights, 'cro', 'supprimer') && ownsCro(cro),
    canExport: allows(rights, 'cro', 'tout'),
  };
};
